const COURSE_TYPE = 'STS Course';
const LESSON_BLOCK = 10;
const SUBJECT_LABELS = {
  math: 'Mathematics',
  english: 'English',
  science: 'Science',
  french: 'French',
  'creative-writing': 'Creative Writing',
};
const SUBJECT_ORDER = ['math', 'english', 'science', 'french', 'creative-writing'];
const PROFILE_KEY = 'sts-registration-profile';
const UNIVERSITY_YEAR_1 = 'university-year-1';

class StsCourseCart extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.productById = new Map();
    this.feeVariantId = null;
    this.currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency: this.dataset.currency || 'CAD',
      maximumFractionDigits: 0,
    });
    this.centsFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency: this.dataset.currency || 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatMoney(value) {
    const amount = Number(value) || 0;
    return Number.isInteger(amount) ? this.currencyFormatter.format(amount) : this.centsFormatter.format(amount);
  }

  connectedCallback() {
    this.searchInput = this.querySelector('[data-course-search]');
    this.gradeSelect = this.querySelector('[data-course-grade]');
    this.subjectSelect = this.querySelector('[data-course-subject]');
    this.campusSelect = this.querySelector('[data-course-campus]');
    this.studentStatusInputs = [...this.querySelectorAll('[data-student-status]')];
    this.resultsContainer = this.querySelector('[data-course-results]');
    this.resultsCount = this.querySelector('[data-results-count]');
    this.resultsContext = this.querySelector('[data-results-context]');
    this.profileNote = this.querySelector('[data-profile-note]');
    this.filterHint = this.querySelector('[data-filter-hint]');
    this.statusRegion = this.querySelector('[data-course-status]');
    this.noResults = this.querySelector('[data-no-results]');
    this.lastSavedNote = null;

    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);

    this.restoreProfile();
    this.loadProducts();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
  }

  onSubmit = (event) => {
    if (event.target.matches('[data-checkout-form]')) {
      this.onCheckoutSubmit(event);
    }
  };

  onInput = (event) => {
    if (event.target === this.searchInput) this.renderResults();
    if (event.target.matches('[data-cart-student-name], [data-cart-note]')) this.queueCartFieldSave(event.target);
  };

  onChange = (event) => {
    if (
      event.target === this.gradeSelect ||
      event.target === this.subjectSelect ||
      event.target === this.campusSelect ||
      event.target.matches('[data-student-status]')
    ) {
      this.renderResults();
    }
    if (event.target.matches('[data-student-status]')) this.syncFee();
    if (event.target.matches('[data-cart-student-name], [data-cart-note]')) this.queueCartFieldSave(event.target, 0);
  };

  onClick = (event) => {
    const clearButton = event.target.closest('[data-clear-filters]');
    if (clearButton) {
      this.clearFilters();
      return;
    }

    const addButton = event.target.closest('[data-add-course]');
    if (addButton) {
      this.addCourse(addButton);
      return;
    }

    const removeButton = event.target.closest('[data-remove-course]');
    if (removeButton) {
      this.removeCourse(removeButton);
      return;
    }

    const stepButton = event.target.closest('[data-lessons-step]');
    if (stepButton) this.stepLessons(stepButton);
  };

  onCheckoutSubmit = (event) => {
    const form = event.target;
    const input = this.querySelector('[data-cart-student-name]');
    const name = input?.value.trim() || '';
    const note = this.querySelector('[data-cart-note]')?.value.trim() || '';

    if (!name) {
      event.preventDefault();
      const status = this.querySelector('[data-student-save-status]');
      if (status) {
        status.textContent = "Add the student's name before checkout.";
        status.classList.add('is-error');
      }
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus({ preventScroll: true });
      return;
    }

    // The checkout form posts to /cart, so mirrored inputs save the name and
    // the note even if the debounced AJAX saves have not flushed yet.
    this.querySelectorAll('[data-student-name-mirror]').forEach((mirror) => {
      mirror.value = name;
    });
    this.querySelectorAll('[data-note-mirror]').forEach((mirror) => {
      mirror.value = note;
    });

    if (form.dataset.stsLinesStamped === 'true') return;

    // Stamp the name onto every line item first, so it shows against each
    // course in the admin order and in the staff order email — not only in the
    // order-level "Additional details".
    event.preventDefault();
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter) submitter.disabled = true;
    this.stampStudentNameOnLines(name)
      .catch(() => {
        /* the cart attribute still carries the name */
      })
      .finally(() => {
        form.dataset.stsLinesStamped = 'true';
        this.submitCheckout(form, submitter);
      });
  };

  submitCheckout(form, submitter) {
    if (submitter) submitter.disabled = false;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter || undefined);
      return;
    }
    // Fallback: form.submit() drops the submit button's name, so carry it.
    if (!form.querySelector('[data-checkout-flag]')) {
      const flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'checkout';
      flag.value = '';
      flag.setAttribute('data-checkout-flag', '');
      form.append(flag);
    }
    form.submit();
  }

  // Line item properties are what the admin shows beneath each course line and
  // what order notification emails can loop over.
  async stampStudentNameOnLines(name) {
    if (!name) return;
    let cart = await this.fetchCart();
    for (let index = 0; index < (cart?.items?.length || 0); index += 1) {
      const item = cart.items[index];
      if ((item.properties || {})['Student name'] === name) continue;
      const properties = { 'Student name': name };
      for (const [key, value] of Object.entries(item.properties || {})) {
        if (key !== 'Student name') properties[key] = value;
      }
      cart = await this.changeCartLine({ line: index + 1, quantity: item.quantity, properties });
    }
  }

  statusFieldFor(field) {
    return field.matches('[data-cart-note]') ? '[data-note-save-status]' : '[data-student-save-status]';
  }

  setSaveStatus(selector, message, isError) {
    const status = this.querySelector(selector);
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', Boolean(message && isError));
  }

  queueCartFieldSave(field, delay = 600) {
    this.setSaveStatus(this.statusFieldFor(field), '', false);
    this.pendingSaveStatuses ||= new Set();
    this.pendingSaveStatuses.add(this.statusFieldFor(field));
    clearTimeout(this.cartFieldTimer);
    this.cartFieldTimer = setTimeout(() => this.saveCartFields(), delay);
  }

  // The name and the note travel in one /cart/update.js call: two overlapping
  // cart writes race, and the later one wins with a stale copy of the other
  // field.
  async saveCartFields() {
    const name = this.querySelector('[data-cart-student-name]')?.value.trim() || '';
    const note = this.querySelector('[data-cart-note]')?.value.trim() || '';
    if (name === this.lastSavedStudentName && note === this.lastSavedNote) return;

    const touched = this.pendingSaveStatuses || new Set();
    this.pendingSaveStatuses = new Set();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Student name': name }, note }),
      });
      if (!response.ok) throw new Error();
      this.lastSavedStudentName = name;
      this.lastSavedNote = note;
      this.querySelectorAll('[data-student-name-mirror]').forEach((mirror) => {
        mirror.value = name;
      });
      this.querySelectorAll('[data-note-mirror]').forEach((mirror) => {
        mirror.value = note;
      });
      for (const selector of touched) {
        const value = selector === '[data-note-save-status]' ? note : name;
        if (value) this.setSaveStatus(selector, 'Saved', false);
      }
    } catch {
      for (const selector of touched) {
        this.setSaveStatus(selector, 'Could not save — will retry at checkout.', true);
      }
    }
  }

  async loadProducts() {
    try {
      const response = await fetch(this.dataset.productsUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Catalog request failed with ${response.status}`);

      const payload = await response.json();
      const catalog = payload.products || [];
      this.feeVariantId = catalog.find((product) => /registration fee/i.test(product.title))?.variants?.[0]?.id || null;
      this.products = catalog.filter((product) => {
        if (/^Summer/i.test(product.title)) return product.product_type === COURSE_TYPE;
        if (/Creative Writing Workshop/i.test(product.title)) return true;
        if (/OSSLT/i.test(product.title)) return true;
        if (/Enrichment Retake/i.test(product.title)) return true;
        if (this.productSubject(product) === 'creative-writing') return false;
        return product.product_type === COURSE_TYPE;
      });
      this.products.sort((a, b) => {
        const subject = SUBJECT_ORDER.indexOf(this.productSubject(a)) - SUBJECT_ORDER.indexOf(this.productSubject(b));
        if (subject !== 0) return subject;
        const gradeA = this.productSortGrade(a);
        const gradeB = this.productSortGrade(b);
        if (gradeA !== gradeB) return gradeA - gradeB;
        const family = this.productSortFamily(a).localeCompare(this.productSortFamily(b));
        if (family !== 0) return family;
        const retake = Number(this.isEnrichmentRetake(a)) - Number(this.isEnrichmentRetake(b));
        if (retake !== 0) return retake;
        return a.title.localeCompare(b.title);
      });
      this.productById = new Map(this.products.map((product) => [String(product.id), product]));
      this.renderResults();
      this.syncFee();
    } catch (error) {
      console.error('[STS course finder]', error);
      this.resultsContainer.innerHTML = '';
      this.resultsCount.textContent = '';
      this.noResults.hidden = false;
      this.statusRegion.textContent = 'The course catalog could not be loaded. Please refresh the page or contact STS for help.';
      this.filterHint.textContent = 'Course catalog unavailable';
    }
  }

  getFilters() {
    return {
      query: this.searchInput?.value.trim().toLowerCase() || '',
      grade: this.gradeSelect?.value || '',
      subject: this.subjectSelect?.value || '',
      campus: this.campusSelect?.value || '',
      studentStatus: this.studentStatusInputs.find((input) => input.checked)?.value || '',
    };
  }

  clearFilters() {
    for (const field of [
      this.searchInput,
      this.gradeSelect,
      this.subjectSelect,
      this.campusSelect,
    ]) {
      if (field) field.value = '';
    }
    this.studentStatusInputs.forEach((input) => {
      input.checked = input.value === 'yes';
    });
    try {
      sessionStorage.removeItem(PROFILE_KEY);
    } catch {
      /* storage unavailable */
    }
    this.renderResults();
    this.syncFee();
    this.searchInput?.focus();
  }

  saveProfile() {
    try {
      sessionStorage.setItem(
        PROFILE_KEY,
        JSON.stringify({
          grade: this.gradeSelect?.value || '',
          campus: this.campusSelect?.value || '',
          studentStatus: this.studentStatusInputs.find((input) => input.checked)?.value || '',
          subject: this.subjectSelect?.value || '',
        })
      );
    } catch {
      /* storage unavailable */
    }
  }

  restoreProfile() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(PROFILE_KEY) || 'null');
    } catch {
      return;
    }
    if (!saved) return;

    if (this.gradeSelect && saved.grade) this.gradeSelect.value = saved.grade;
    if (this.campusSelect && saved.campus) this.campusSelect.value = saved.campus;
    if (this.subjectSelect && saved.subject) this.subjectSelect.value = saved.subject;
    if (saved.studentStatus) {
      const radio = this.studentStatusInputs.find((input) => input.value === saved.studentStatus);
      if (radio) radio.checked = true;
    }
  }

  renderResults() {
    if (!this.resultsContainer || !this.products.length) return;

    const filters = this.getFilters();
    this.saveProfile();
    const matches = this.products.filter((product) => this.productMatches(product, filters));

    const contextParts = [];
    if (filters.grade) contextParts.push(this.gradeFilterLabel(filters.grade));
    if (filters.campus) contextParts.push(filters.campus);
    if (filters.studentStatus) contextParts.push(filters.studentStatus === 'yes' ? 'New student' : 'Returning student');
    if (this.resultsContext) {
      this.resultsContext.hidden = contextParts.length === 0;
      this.resultsContext.textContent = contextParts.join(' · ');
    }
    if (this.profileNote) {
      this.profileNote.hidden = Boolean(filters.campus && filters.studentStatus);
    }
    if (this.profilePromptActive && filters.campus && filters.studentStatus) {
      this.profilePromptActive = false;
      this.setStatus('');
    }

    this.resultsCount.textContent = `${matches.length} ${matches.length === 1 ? 'course' : 'courses'}`;
    this.noResults.hidden = matches.length !== 0;
    this.resultsContainer.hidden = matches.length === 0;
    this.resultsContainer.innerHTML = matches.map((product) => this.renderCourseRow(product, filters)).join('');

    const activeFilters = [filters.grade, filters.subject, filters.campus, filters.query].filter(Boolean).length;
    if (!activeFilters) {
      this.filterHint.textContent = 'Browse all STS courses, or use the filters to find the right course.';
    } else {
      this.filterHint.textContent = `${matches.length} ${matches.length === 1 ? 'match' : 'matches'} for your selections.`;
    }
  }

  productMatches(product, filters) {
    const subjectLabel = this.productSubjectLabel(product);
    if (filters.query && !`${product.title} ${subjectLabel}`.toLowerCase().includes(filters.query)) return false;
    if (filters.subject && subjectLabel !== filters.subject) return false;

    if (filters.grade) {
      if (filters.grade === UNIVERSITY_YEAR_1) {
        if (!this.isUniversityCalculus(product.title)) return false;
      } else {
        const grade = Number(filters.grade);
        const range = this.getGradeRange(product.title);
        if (!range || grade < range[0] || grade > range[1]) return false;
      }
    }

    if (filters.campus && this.hasCampusOption(product)) {
      const hasCampus = product.variants.some((variant) => this.variantCampus(variant, product) === this.normalizeCampus(filters.campus));
      if (!hasCampus) return false;
    }

    return true;
  }

  renderCourseRow(product, filters) {
    const subject = this.productSubjectLabel(product);
    const gradeRange = this.getGradeRange(product.title);
    const gradeLabel = this.isUniversityCalculus(product.title)
      ? 'University Year 1'
      : gradeRange
        ? (gradeRange[0] === gradeRange[1] ? `Grade ${gradeRange[0]}` : `Grades ${gradeRange[0]}–${gradeRange[1]}`)
        : 'Multiple grades';
    const variant = this.findVariant(product, filters);
    const campusRequired = this.hasCampusOption(product) && !filters.campus;
    const statusRequired = !filters.studentStatus;
    const reason = campusRequired
      ? 'Choose a campus to add course'
      : statusRequired
        ? 'Choose new or returning student above'
        : variant
          ? ''
          : 'Not offered for your selections';
    const unitPrice = Number(variant?.price || product.variants?.[0]?.price || 0);
    const isPerLesson = product.product_type === COURSE_TYPE;
    const blockPrice = isPerLesson ? unitPrice * LESSON_BLOCK : unitPrice;
    const priceLabel = this.productPriceLabel(product, isPerLesson);
    const subjectClass = this.escapeAttribute(this.productSubject(product) || 'course');
    const displayTitle = this.displayTitle(product.title);

    return `
      <article class="sts-course-row sts-course-row--${subjectClass}">
        <span class="sts-course-row__dot" aria-hidden="true"></span>
        <div class="sts-course-row__info">
          <h3><a href="/products/${this.escapeAttribute(product.handle)}">${this.escapeHTML(displayTitle)}</a></h3>
          <p class="sts-course-row__meta">
            <span class="sts-course-row__subject">${this.escapeHTML(subject)}</span>
            <span>${this.escapeHTML(gradeLabel)}</span>
            ${filters.campus ? `<span>${this.escapeHTML(filters.campus)}</span>` : ''}
          </p>
        </div>
        <div class="sts-course-row__price">
          <small>${this.escapeHTML(priceLabel)}</small>
          <strong>${this.formatMoney(blockPrice)}</strong>
        </div>
        <button
          class="sts-course-row__add${reason ? ' is-blocked' : ''}"
          type="button"
          data-add-course
          data-product-id="${this.escapeAttribute(product.id)}"
          ${reason ? `data-blocked-reason="${this.escapeAttribute(reason)}" aria-disabled="true" title="${this.escapeAttribute(reason)}"` : ''}
        >
          <span>Add</span>
          ${this.arrowIcon()}
        </button>
      </article>`;
  }

  async addCourse(button) {
    const product = this.productById.get(button.dataset.productId);
    if (!product) return;

    if (button.dataset.blockedReason) {
      this.promptForMissingProfile(button.dataset.blockedReason);
      return;
    }

    const filters = this.getFilters();
    const variant = this.findVariant(product, filters);
    if (!variant) {
      this.setStatus('That course option is not available. Try another campus or student status.', true);
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    button.querySelector('span').textContent = 'Adding…';
    this.setStatus('');

    const studentName = this.querySelector('[data-cart-student-name]')?.value.trim() || '';
    const properties = {};
    if (studentName) properties['Student name'] = studentName;
    properties['Student grade'] = filters.grade ? this.gradeFilterLabel(filters.grade) : '';

    try {
      const items = [{ id: variant.id, quantity: product.product_type === COURSE_TYPE ? LESSON_BLOCK : 1, properties }];
      if (filters.studentStatus === 'yes' && this.feeVariantId && !(await this.cartHasVariant(this.feeVariantId))) {
        items.push({ id: this.feeVariantId, quantity: 1 });
      }

      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: this.dataset.sectionId,
          sections_url: this.dataset.cartUrl,
        }),
      });
      const payload = await this.readJson(response);
      if (!response.ok || !payload) {
        throw new Error(payload?.description || 'The course could not be added. Please refresh the page and try again.');
      }

      this.renderEnrollment(payload.sections?.[this.dataset.sectionId]);
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.querySelector('span').textContent = 'Added ✓';
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-added');
        button.querySelector('span').textContent = 'Add';
      }, 1400);
    } catch (error) {
      this.setStatus(error.message || 'The course could not be added. Please try again.', true);
      button.disabled = false;
      button.classList.remove('is-loading');
      button.querySelector('span').textContent = 'Add';
    }
  }

  // A course is sold per campus, so the campus picker has to be answered before
  // a course can be added. Say so and jump to the picker rather than leaving a
  // dead button on the row.
  promptForMissingProfile(reason) {
    const filters = this.getFilters();
    const target = !filters.campus
      ? this.campusSelect
      : !filters.studentStatus
        ? this.studentStatusInputs[0]
        : null;

    this.setStatus(reason, true);
    this.profilePromptActive = Boolean(target);
    if (!target) return;

    const cluster = target.closest('.sts-student-cluster') || target;
    cluster.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.focus({ preventScroll: true });

    const flash = target.matches('select') ? target : cluster;
    flash.classList.add('is-attention');
    clearTimeout(this.attentionTimer);
    this.attentionTimer = setTimeout(() => flash.classList.remove('is-attention'), 2400);
  }

  async removeCourse(button) {
    const line = button.closest('[data-cart-line], [data-fee-line]');
    if (!line) return;

    button.disabled = true;
    line.classList.add('is-updating');

    try {
      let cart = await this.changeCartLine({
        id: line.dataset.lineKey,
        quantity: 0,
        sections: this.dataset.sectionId,
        sections_url: this.dataset.cartUrl,
      });

      // If only the registration fee is left, remove it too.
      const remaining = cart.items || [];
      if (this.feeVariantId && remaining.length && remaining.every((item) => item.variant_id === this.feeVariantId)) {
        for (const item of remaining) {
          cart = await this.changeCartLine({
            id: item.key,
            quantity: 0,
            sections: this.dataset.sectionId,
            sections_url: this.dataset.cartUrl,
          });
        }
      }

      this.renderEnrollment(cart.sections?.[this.dataset.sectionId]);
    } catch (error) {
      line.classList.remove('is-updating');
      button.disabled = false;
      this.setStatus(error.message || 'The course could not be removed.', true);
    }
  }

  async stepLessons(button) {
    const line = button.closest('[data-cart-line]');
    if (!line) return;

    const current = Number(line.dataset.quantity || 0);
    const delta = Number(button.dataset.lessonsStep || 0);
    let next = Math.round((current + delta) / LESSON_BLOCK) * LESSON_BLOCK;
    next = Math.max(LESSON_BLOCK, next);
    if (next === current) return;

    button.disabled = true;
    line.classList.add('is-updating');

    try {
      const cart = await this.changeCartLine({
        id: line.dataset.lineKey,
        quantity: next,
        sections: this.dataset.sectionId,
        sections_url: this.dataset.cartUrl,
      });
      this.renderEnrollment(cart.sections?.[this.dataset.sectionId]);
    } catch (error) {
      line.classList.remove('is-updating');
      button.disabled = false;
      this.setStatus(error.message || 'The lesson count could not be updated.', true);
    }
  }

  async cartHasVariant(variantId) {
    const cart = await this.fetchCart();
    return (cart?.items || []).some((item) => item.variant_id === variantId);
  }

  async fetchCart() {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  // Keep the registration-fee line in step with the New/Returning toggle.
  async syncFee() {
    if (!this.feeVariantId || this.feeSyncing) return;
    const status = this.getFilters().studentStatus;
    if (!status) return;

    this.feeSyncing = true;
    try {
      const cart = await this.fetchCart();
      if (!cart) return;
      const feeItem = (cart.items || []).find((item) => item.variant_id === this.feeVariantId);
      const hasCourses = (cart.items || []).some((item) => item.variant_id !== this.feeVariantId);

      if (status === 'yes' && hasCourses && !feeItem) {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.feeVariantId, quantity: 1 }],
            sections: this.dataset.sectionId,
            sections_url: this.dataset.cartUrl,
          }),
        });
        const payload = await this.readJson(response);
        if (response.ok && payload) this.renderEnrollment(payload.sections?.[this.dataset.sectionId]);
      } else if (status === 'no' && feeItem) {
        const updated = await this.changeCartLine({
          id: feeItem.key,
          quantity: 0,
          sections: this.dataset.sectionId,
          sections_url: this.dataset.cartUrl,
        });
        this.renderEnrollment(updated.sections?.[this.dataset.sectionId]);
      }
    } catch {
      /* leave the cart as-is on network hiccups */
    } finally {
      this.feeSyncing = false;
    }
  }

  renderEnrollment(sectionHtml) {
    if (!sectionHtml) {
      window.location.reload();
      return;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newAside = doc.querySelector('.sts-enrollment');
    const currentAside = this.querySelector('.sts-enrollment');

    // Carry the (possibly unsaved) student name and note through the swap.
    const currentNameInput = this.querySelector('[data-cart-student-name]');
    const typedName = currentNameInput?.value ?? null;
    const nameHadFocus = currentNameInput === document.activeElement;
    const currentNoteField = this.querySelector('[data-cart-note]');
    const typedNote = currentNoteField?.value ?? null;
    const noteHadFocus = currentNoteField === document.activeElement;

    if (newAside && currentAside) currentAside.replaceWith(newAside);

    if (typedName !== null) {
      const newNameInput = this.querySelector('[data-cart-student-name]');
      if (newNameInput) {
        newNameInput.value = typedName;
        if (nameHadFocus) {
          newNameInput.focus({ preventScroll: true });
          newNameInput.setSelectionRange(typedName.length, typedName.length);
        }
      }
    }

    if (typedNote !== null) {
      const newNoteField = this.querySelector('[data-cart-note]');
      if (newNoteField) {
        newNoteField.value = typedNote;
        if (noteHadFocus) {
          newNoteField.focus({ preventScroll: true });
          newNoteField.setSelectionRange(typedNote.length, typedNote.length);
        }
      }
    }

    const newBar = doc.querySelector('.sts-enrollment-mobile-bar');
    const currentBar = this.querySelector('.sts-enrollment-mobile-bar');
    if (newBar && currentBar) currentBar.replaceWith(newBar);
    else if (newBar) this.append(newBar);
    else currentBar?.remove();

    const count = this.querySelector('.sts-enrollment__count');
    if (count) {
      count.classList.add('is-pop');
      count.addEventListener('animationend', () => count.classList.remove('is-pop'), { once: true });
    }
  }

  async changeCartLine(payload) {
    const response = await fetch(this.dataset.cartChangeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const cart = await this.readJson(response);
    if (!response.ok || !cart) {
      throw new Error(cart?.description || 'The cart could not be updated. Please refresh the page and try again.');
    }
    return cart;
  }

  // Responses are not always JSON (e.g. an expired dev-session proxy replies with plain text).
  async readJson(response) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  findVariant(product, filters) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available !== false);
    // The legacy five-band workshop products include the old registration-fee
    // uplift inside their Yes variant. The new registration flow manages that
    // fee as a separate line, so use the base (No) workshop variant here.
    const studentStatus = product.product_type === COURSE_TYPE ? filters.studentStatus : 'no';
    return availableVariants.find((variant) => {
      if (this.hasCampusOption(product) && filters.campus) {
        if (this.variantCampus(variant, product) !== this.normalizeCampus(filters.campus)) return false;
      }
      if (this.hasStudentStatusOption(product) && studentStatus) {
        if (this.variantStudentStatus(variant, product) !== studentStatus) return false;
      }
      return true;
    });
  }

  productSubject(product) {
    const tag = (product.tags || []).find((entry) => entry.startsWith('sts-subject:'));
    if (tag) return tag.slice('sts-subject:'.length);
    if (/creative writing/i.test(product.title)) return 'creative-writing';
    if (/^math$/i.test(product.product_type)) return 'math';
    if (/^english$/i.test(product.product_type)) return 'english';
    if (/^science$/i.test(product.product_type)) return 'science';
    if (/^french$/i.test(product.product_type)) return 'french';
    return '';
  }

  productSubjectLabel(product) {
    return SUBJECT_LABELS[this.productSubject(product)] || 'STS course';
  }

  getOptionIndex(product, matcher) {
    return (product.options || []).findIndex((option) => matcher.test(option.name || ''));
  }

  hasCampusOption(product) {
    return this.getOptionIndex(product, /campus/i) !== -1;
  }

  hasStudentStatusOption(product) {
    return this.getOptionIndex(product, /new student/i) !== -1;
  }

  variantCampus(variant, product) {
    const index = this.getOptionIndex(product, /campus/i);
    return index === -1 ? '' : this.normalizeCampus(variant[`option${index + 1}`]);
  }

  variantStudentStatus(variant, product) {
    const index = this.getOptionIndex(product, /new student/i);
    const value = index === -1 ? '' : String(variant[`option${index + 1}`] || '').toLowerCase();
    return value === 'yes' ? 'yes' : value === 'no' ? 'no' : '';
  }

  normalizeCampus(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  getGradeRange(title) {
    if (/OSSLT/i.test(String(title || ''))) return [10, 10];
    const match = title.match(/Grades?\s*(\d{1,2})(?:\s*(?:-|–|&|to)\s*(\d{1,2}))?/i);
    if (!match) return null;
    const first = Number(match[1]);
    const second = Number(match[2] || match[1]);
    return [Math.min(first, second), Math.max(first, second)];
  }

  isUniversityCalculus(title) {
    return /university calculus|u-calculus/i.test(String(title || ''));
  }

  productSortGrade(product) {
    if (this.isUniversityCalculus(product.title)) return 13;
    return this.getGradeRange(product.title)?.[0] ?? 99;
  }

  productSortFamily(product) {
    return this.displayTitle(product.title)
      .replace(/\s+[—–-]\s+(?:University Preparation|Enrichment Retake).*$/i, '')
      .replace(/\b(?:Academic|De-streamed)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  isEnrichmentRetake(product) {
    return /Enrichment Retake/i.test(String(product?.title || ''));
  }

  gradeFilterLabel(value) {
    return value === UNIVERSITY_YEAR_1 ? 'University Year 1' : `Grade ${value}`;
  }

  productPriceLabel(product, isPerLesson) {
    if (isPerLesson) return `${LESSON_BLOCK} lessons`;
    if (/Creative Writing Workshop/i.test(product.title)) return '20 lessons';
    if (/OSSLT/i.test(product.title)) return 'Per course';
    if (/Enrichment Retake/i.test(product.title)) return 'Per course';
    return 'Course fee';
  }

  displayTitle(title) {
    const value = String(title || '').replace(/\s+-\s+\d+\s+Lessons?(?:\/course)?$/i, '').trim();
    if (/creative writing/i.test(value) && !/^Summer/i.test(value)) {
      if (/Grade\s*(?:3|4)|Primary|Beginner|Foundation/i.test(value)) return 'Foundation — Grades 3–4 Creative Writing Workshop';
      if (/Grade\s*(?:5|6)|Junior/i.test(value)) return 'Junior — Grades 5–6 Creative Writing Workshop';
      if (/Grade\s*(?:7|8)|Elementary/i.test(value)) return 'Elementary — Grades 7–8 Creative Writing Workshop';
      if (/Grade\s*(?:9|10)|Intermediate/i.test(value)) return 'Intermediate — Grades 9–10 Creative Writing Workshop';
      if (/Grade\s*(?:11|12)|Senior/i.test(value)) return 'Senior — Grades 11–12 Creative Writing Workshop';
    }
    if (/enrichment retake/i.test(value)) {
      if (/grade 11 math/i.test(value)) return 'Grade 11 Functions — Enrichment Retake';
      if (/grade 12 calculus/i.test(value) && /vector/i.test(value)) return 'Grade 12 Calculus & Vectors — Enrichment Retake';
      if (/grade 12 advanced functions/i.test(value)) return 'Grade 12 Advanced Functions — Enrichment Retake';
      if (/grade 12 data management/i.test(value)) return 'Grade 12 Data Management — Enrichment Retake';
      return value.replace(/\s+-\s+Enrichment Retake$/i, ' — Enrichment Retake');
    }
    if (/university calculus|u-calculus/i.test(value)) return 'University Calculus Preparation — AP/IB/Year 1';
    if (/grade 12 calculus/i.test(value) && /vector/i.test(value)) return 'Grade 12 Calculus & Vectors — University Preparation';
    if (/grade 12 advanced functions/i.test(value)) return 'Grade 12 Advanced Functions — University Preparation';
    if (/grade 12 data management/i.test(value)) return 'Grade 12 Data Management — University Preparation';
    if (/grade 11 math|grade 11 functions/i.test(value)) return 'Grade 11 Functions — University Preparation';
    if (/grade 11/i.test(value) && /english/i.test(value)) return 'Grade 11 English — University Preparation';
    if (/grade 12/i.test(value) && /english/i.test(value)) return 'Grade 12 English — University Preparation';
    if (/osslt/i.test(value)) return 'OSSLT Preparation';
    if (/bfsl/i.test(value)) return 'BFSL — Beginner Level';
    if (/fsl/i.test(value) && /level\s*1/i.test(value)) return 'FSL — Level 1';
    if (/fsl/i.test(value) && /level\s*2/i.test(value)) return 'FSL — Level 2';
    if (/fsl/i.test(value) && /level\s*3/i.test(value)) return 'FSL — Level 3';
    if (/fsl/i.test(value) && /level\s*4/i.test(value)) return 'FSL — Level 4';
    return value;
  }

  setStatus(message, isError = false) {
    if (!this.statusRegion) return;
    this.statusRegion.textContent = message;
    this.statusRegion.classList.toggle('is-error', Boolean(message && isError));
  }

  arrowIcon() {
    return '<svg aria-hidden="true" viewBox="0 0 20 20"><path d="M4 10h11M11 5l5 5-5 5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/></svg>';
  }

  escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHTML(value).replaceAll('`', '&#096;');
  }
}

if (!customElements.get('sts-course-cart')) {
  customElements.define('sts-course-cart', StsCourseCart);
}
